import React, { useState } from "react";
import Icon from "../Icon";
import { fmtCcy } from "../helpers";
import { ExplanationClass, ExplanationPayment } from "../types";

const AMOUNT_TOLERANCE = 0.01;
const UNITS_TOLERANCE = 0.0001;
const COPIED_MS = 1500;

const units = (value: number | null): string =>
  value == null ? "—" : value.toFixed(6);

const paymentCount = (count: number): string =>
  `${count} ${count === 1 ? "payment" : "payments"}`;

const signed = (value: number, format: (value: number) => string): string =>
  `${value > 0 ? "+" : value < 0 ? "−" : ""}${format(Math.abs(value))}`;

const splitVerdict = (
  amountGap: number,
  unitsGap: number,
  amountOff: boolean,
  unitsOff: boolean,
): string => {
  if (amountOff)
    return `GHS ${fmtCcy(Math.abs(amountGap))} ${
      amountGap < 0 ? "short" : "over"
    }`;
  if (unitsOff) return `units differ by ${Math.abs(unitsGap).toFixed(6)}`;
  return "matches the row";
};

const classPrice = (group: ExplanationClass): number | null =>
  group.payments.find((payment) => payment.price != null)?.price ?? null;

const TaId: React.FC<{ taId: number | null }> = ({ taId }) => {
  const [copied, setCopied] = useState(false);
  if (taId == null) return <span className="breakdown-muted">—</span>;

  const copy = (): void => {
    navigator.clipboard
      ?.writeText(String(taId))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_MS);
      })
      .catch(() => setCopied(false));
  };

  return (
    <button
      type="button"
      className="breakdown-ta-id"
      onClick={copy}
      title="Copy TA ID"
    >
      {taId}
      <Icon name={copied ? "check" : "copy"} size={11} strokeWidth={2.2} />
    </button>
  );
};

const Investor: React.FC<{ payment: ExplanationPayment }> = ({ payment }) => (
  <span className="breakdown-investor" title={payment.details || ""}>
    {payment.reference || payment.details || "—"}
  </span>
);

interface Props {
  classes: ExplanationClass[];
  narration: string | null;
  rowAmount: number;
  rowUnits: number | null;
}

const PaymentBreakdown: React.FC<Props> = ({
  classes,
  narration,
  rowAmount,
  rowUnits,
}) => {
  const totalAmount = classes.reduce((total, group) => total + group.amount, 0);
  const totalUnits = classes.reduce((total, group) => total + group.units, 0);
  const amountGap = Math.round((totalAmount - rowAmount) * 100) / 100;
  const unitsGap = rowUnits == null ? 0 : totalUnits - rowUnits;
  const amountOff = Math.abs(amountGap) > AMOUNT_TOLERANCE;
  const unitsOff = Math.abs(unitsGap) > UNITS_TOLERANCE;
  const showsReference = classes.some((group) =>
    group.payments.some((payment) => payment.reference),
  );
  const count = classes.reduce((total, group) => total + group.count, 0);
  const isSplit = count > 1 || showsReference;
  const settled = !amountOff && !unitsOff;

  return (
    <div className="breakdown">
      {isSplit && (
        <div className="breakdown-header">
          <div className="breakdown-summary">
            <strong>Split deposit</strong>
            <span>
              {" "}
              · {paymentCount(count)}
              {classes.length > 1 && ` across ${classes.length} classes`} ·{" "}
            </span>
            <span className={settled ? "is-settled" : "is-off"}>
              {splitVerdict(amountGap, unitsGap, amountOff, unitsOff)}
              {settled && <Icon name="check" size={12} strokeWidth={2.5} />}
            </span>
          </div>
          {narration && showsReference && (
            <div className="breakdown-narration">
              <span className="breakdown-muted">Shared narration</span>
              <span className="mono">{narration}</span>
            </div>
          )}
        </div>
      )}
      <div className="breakdown-scroll">
        <table className="breakdown-table">
          <thead>
            <tr>
              <th>TA ID</th>
              <th>{showsReference ? "Investor" : "Narration"}</th>
              <th className="num">Amount (GHS)</th>
              <th className="num">Units</th>
            </tr>
          </thead>
          {classes.map((group) => {
            const price = classPrice(group);
            return (
              <tbody key={group.label}>
                <tr className="breakdown-class">
                  <th colSpan={2} scope="rowgroup">
                    <span>{group.label}</span>
                    <span className="breakdown-muted">
                      {" "}
                      · {paymentCount(group.count)}
                      {price != null && ` · price ${price.toFixed(6)}`}
                    </span>
                  </th>
                  <td className="num">{fmtCcy(group.amount)}</td>
                  <td className="num">{units(group.units)}</td>
                </tr>
                {group.payments.map((payment, index) => (
                  <tr key={payment.taId ?? `${group.label}-${index}`}>
                    <td>
                      <TaId taId={payment.taId} />
                    </td>
                    <td>
                      <Investor payment={payment} />
                    </td>
                    <td className="num">{fmtCcy(payment.amount)}</td>
                    <td className="num">
                      {units(payment.units)}
                      {payment.price != null && payment.price !== price && (
                        <span className="breakdown-muted">
                          {" "}
                          @ {payment.price.toFixed(6)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            );
          })}
          <tfoot>
            <tr>
              <th colSpan={2}>Payments total</th>
              <td className="num">{fmtCcy(totalAmount)}</td>
              <td className="num">{units(totalUnits)}</td>
            </tr>
            <tr>
              <th colSpan={2}>Statement row</th>
              <td className={amountOff ? "num is-off" : "num"}>
                {fmtCcy(rowAmount)}
              </td>
              <td className={unitsOff ? "num is-off" : "num"}>
                {units(rowUnits)}
              </td>
            </tr>
            <tr
              className={
                amountOff || unitsOff ? "breakdown-gap is-off" : "breakdown-gap"
              }
            >
              <th colSpan={2}>
                {amountOff || unitsOff ? (
                  "Difference"
                ) : (
                  <>
                    <Icon name="check" size={12} strokeWidth={2.5} /> Payments
                    match the statement row
                  </>
                )}
              </th>
              <td className="num">
                {amountOff ? signed(amountGap, fmtCcy) : ""}
              </td>
              <td className="num">
                {unitsOff ? signed(unitsGap, (value) => value.toFixed(6)) : ""}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PaymentBreakdown;
